<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import NoiseReductionComparison from '$lib/PageSections/DataDrivenResults/NoiseReductionComparison.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import privacyGradeDiyWindowInsertImg from './images/privacyGradeDiyWindowInsert.png';
	import windowBuildsYT from './images/window-blinds-yt.jpg?w=610';
	import womanInstallingSoundproofInsertsImg from './images/womanInstallingSoundproofInserts.jpg';
	import womanMeasuringHistoricWindowsImg from './images/womanMeasuringHistoricWindows.jpg';
</script>

<Section>
	<p>
		If your windows are letting in too much noise, or drafts, or light, you need
		an upgrade. Living with unwanted light and sound, especially when you're
		stuck inside, can make you feel out of control and not at home in your
		house.
	</p>
	<Bubble>
		{#snippet titleSlot()}
			<p class="text-lg font-bold">The most common window treatments are:</p>
		{/snippet}
		<ul>
			<li>
				Adding curtains or blinds, which either block some light, or all light
				and some sound
			</li>
			<li>Getting replacement windows</li>
			<li>Replacing the windows yourself, if you are a skilled DIYer</li>
		</ul>
	</Bubble>
	<p>
		Curtains, blinds, and window replacement can all require contractors or
		professional installers coming into your home. They would need access to any
		windows you want to upgrade, and in the case of replacement windows, they
		would need to access your wall, opening it up before doing any repairs or
		updates.
	</p>
</Section>
<Section>
	<h2>Watch How Long It Takes to Install Inserts vs Blinds</h2>
	<Youtube
		src="https://www.youtube.com/embed/0mK5ak-RNJo"
		title="Window Inserts vs Window Blinds Installation"
		posterImageSrc={windowBuildsYT}
	/>
	<p>
		A better option is diy window inserts which can be installed from the
		comfort of your house to add privacy and energy savings without blocking
		your windows. If you want more control over the light and noise coming
		through your windows, but still like the way they look, think about adding
		window inserts.
	</p>
	<Image
		src={womanInstallingSoundproofInsertsImg}
		alt="woman installing diy window inserts to add noise noise blocking without blocking light"
	/>
</Section>

<Section>
	<h2>DIY Window Inserts</h2>
	<p>
		Our window inserts can all be self installed. They just push into the
		interior of your window frame without mounting hardware or altering your
		original window. The window insert is custom made to fit your window
		precisely and mailed to your home or business so the whole interaction is
		contactless. We even have a contactless measurement process.
	</p>
	<Image
		src={womanMeasuringHistoricWindowsImg}
		alt="woman using diy measurement system old farmhouse windows for self install diy window inserts"
	/>
	<p>
		All of our diy window treatments block noise and drafts, but some grade
		types have extra benefits, like <a href="/products/privacy-inserts"
			>translucent panel for added privacy</a
		>
		or
		<a href="/products/uv-blocking-window-inserts"
			>UV protection to stop wood floors and furniture from fading</a
		>.
	</p>
	<NoiseReductionComparison withDefaultDescription>
		{#snippet footer()}
			<p>
				Once the window inserts are installed, you can still add blind or
				curtains—they're a great way to dress up any room. With diy window
				inserts, you won't need to rely on curtains for privacy or noise
				control, so you can add whatever style you like.
			</p>
		{/snippet}
	</NoiseReductionComparison>
	<Bubble title="How to Measure & Install DIY Window Inserts">
		<ul>
			<li>
				We ship you a laser measurement kit (thoroughly cleaned between each
				use)
			</li>
			<li>You measure your windows at home</li>
			<li>
				Enter window dimensions into our online portal (our proprietary software
				and in-house window experts check the dimensions to ensure a perfect
				fit)
			</li>
			<li>We create your custom window insert & ship it to you</li>
			<li>You install it at home by pressing it into your window</li>
		</ul>
	</Bubble>
	<p>
		Your window measurements work for each of these grade types: standard,
		noise-blocking, UV-blocking, privacy-grade. You can decide <a
			href="/products/indowone-window-inserts"
			>which grade of custom window insert</a
		> you want at any point.
	</p>
	<Image
		src={privacyGradeDiyWindowInsertImg}
		alt="privacy grade diy window insert installed in a bedroom window right next to bed"
	/>
</Section>

<Section>
	<h2>Window Insert Cost</h2>
	<p>
		The cost of our self-install window inserts depends on the size of your
		window and the grade type you choose. A standard-sized window averages $368
		or $31/sf. When you choose to install yourself, you are skipping
		installation fees and the trouble of construction, and you're also making
		the entire process contactless.
	</p>
</Section>
<Reviews />
